import { PATHS } from "../constants/paths"
import { useAppDispatch, useAppSelector } from "../store"
import { setUserLoading } from "../store/userReducer"
import {
  connectHandler,
  disconnectHandler,
  getMetaMaskStatus,
  handleAccountsChanged,
  isMobile,
  restoreSession,
} from "../utils/connectHandler"
import DrawerMenu from "./DrawerMenu"
import CtaButton from "./button/CtaButton"
import { MenuList } from "./layout/MenuList"
import LoadingButton from "@mui/lab/LoadingButton"
import { Tooltip } from "@mui/material"
import Button from "@mui/material/Button"
import useMediaQuery from "@mui/material/useMediaQuery"
import React, { useEffect, useState } from "react"
import { NavLink, useLocation, useNavigate } from "react-router-dom"
import { useDialog } from "../context/DialogContext"
import ZealyRewardDialog from "./dialog/ZealyRewardDialog"

export const MainNav = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const { user, loading } = useAppSelector((state) => state.user)
  const [metaMaskStatus, setMetaMaskStatus] = useState({
    installed: false,
    unlocked: false,
    hasAccounts: false,
  })

  // Показать диалог приза Zealy
  const [openZealyRewardDialog, closeZealyRewardDialog] = useDialog()

  // Проверка статуса MetaMask при загрузке
  useEffect(() => {
    const checkMetaMaskStatus = async () => {
      const status = await getMetaMaskStatus()
      setMetaMaskStatus(status)

      // Если MetaMask установлен и разблокирован, восстанавливаем сессию
      if (status.installed && status.unlocked) {
        restoreSession(dispatch)
      } else {
        // Если MetaMask заблокирован или не установлен, очищаем загрузку
        dispatch(setUserLoading(false))
      }
    }

    checkMetaMaskStatus()

    const accountsChangedHandler = (accounts) =>
      handleAccountsChanged(accounts, dispatch)

    // Слушаем изменения аккаунтов
    if (window.ethereum) {
      window.ethereum.on("accountsChanged", accountsChangedHandler)
      // Также слушаем события разблокировки
      window.ethereum.on("connect", () => {
        setMetaMaskStatus((prev) => ({ ...prev, unlocked: true }))
      })
      window.ethereum.on("disconnect", () => {
        setMetaMaskStatus((prev) => ({ ...prev, unlocked: false }))
      })
    }

    return () => {
      if (window.ethereum) {
        window.ethereum.removeListener(
          "accountsChanged",
          accountsChangedHandler,
        )
        window.ethereum.removeListener("connect", () => {})
        window.ethereum.removeListener("disconnect", () => {})
      }
    }
  }, [dispatch])

  const getWallet = async () => {
    // Если MetaMask не установлен
    if (!metaMaskStatus.installed) {
      if (isMobile()) {
        openMetaMaskMobile()
      } else {
        alert("Please install MetaMask!")
        window.open("https://metamask.io/download/", "_blank")
      }
      return
    }

    // Если MetaMask установлен, но заблокирован
    if (metaMaskStatus.installed && !metaMaskStatus.unlocked) {
      dispatch(setUserLoading(true))
      try {
        const [address, zealy_reward] = await connectHandler(dispatch)
        if (zealy_reward) {
          openZealyRewardDialog({
            children: (
              <ZealyRewardDialog
                item={zealy_reward.item}
                amount={zealy_reward.amount}
                closeDialog={closeZealyRewardDialog}
              />
            ),
          })
        }
      } catch (error) {
        console.error("Connection error:", error)
      } finally {
        dispatch(setUserLoading(false))
      }

      return
    }

    // проверяем сессию
    try {
      const [address, zealy_reward] = await connectHandler(dispatch)
      if (zealy_reward) {
        openZealyRewardDialog({
          children: (
            <ZealyRewardDialog
              item={zealy_reward.item}
              amount={zealy_reward.amount}
              closeDialog={closeZealyRewardDialog}
            />
          ),
        })
      }
    } catch (error) {
      console.error("Connection error:", error)
    } finally {
      dispatch(setUserLoading(false))
    }
  }

  const disconnect = async () => {
    try {
      const result = await disconnectHandler(dispatch, user.sessionToken)
      if (result && result.success) {
        navigate("/")
      }
    } catch (error) {
      console.error("Disconnect error:", error)
      navigate("/")
    }
  }

  const openMetaMaskMobile = () => {
    window.location.href =
      "https://metamask.app.link/dapp/game-bsc-three.vercel.app/"
  }

  const send = () => {
    if (isMobile()) {
      window.open("https://metamask.app.link/dapp/game-bsc-three.vercel.app/")
    }
    navigate(`${location.pathname}?popup=deposit`)
  }

  const isDownLg = useMediaQuery("(max-width: 1280px)")
  const isDownMd = useMediaQuery("(max-width: 900px)")
  const isDownSm = useMediaQuery("(max-width: 600px)")

  // Определяем текст и состояние кнопки подключения
  const getConnectButtonState = () => {
    if (!metaMaskStatus.installed) {
      return {
        text: "Install MetaMask",
        disabled: false,
        tooltip: "MetaMask not installed",
      }
    }

    if (!metaMaskStatus.unlocked) {
      return {
        text: "Connect Wallet",
        disabled: false,
        tooltip: "MetaMask is locked",
      }
    }

    return {
      text: "Connect Wallet",
      disabled: false,
      tooltip: "",
    }
  }

  const connectButtonState = getConnectButtonState()
  return (
    <nav className="nav">
      <ul className="nav-list">
        {!isDownLg && (
          <>
            {Object.entries(PATHS).map(([key, value], index) => (
              <React.Fragment key={index}>
                {value.TYPE === "public" && value.SUB_TYPE === "main" && (
                  <>
                    {key !== "NFT_BOXES" &&
                    key !== "STAKING" &&
                    key !== "STAKING_NFT" ? (
                      <>
                        {!value.DISABLED ? (
                          <li className="nav-item">
                            {/^https?:\/\//.test(value.PATH) ? (
                              <Button
                                className="nav-link"
                                component="a"
                                href={value.PATH}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {value.LABEL}
                              </Button>
                            ) : (
                              <Button
                                className="nav-link"
                                component={NavLink}
                                to={value.PATH}
                              >
                                {value.LABEL}
                              </Button>
                            )}
                          </li>
                        ) : (
                          <Tooltip title={"Coming soon..."}>
                            <span>
                              <li className="nav-item">
                                <Button
                                  className="nav-link"
                                  component={NavLink}
                                  to={value.PATH}
                                  disabled={value.DISABLED}
                                >
                                  {value.LABEL}
                                </Button>
                              </li>
                            </span>
                          </Tooltip>
                        )}
                      </>
                    ) : key === "NFT_BOXES" ? (
                      <>
                        <li className="nav-item">
                          <MenuList
                            name="NFT Boxes"
                            items={[
                              {
                                label: value.NFT_BOXES_WITH_TURRETS.LABEL,
                                link: value.NFT_BOXES_WITH_TURRETS.PATH,
                              },
                              {
                                label: value.NFT_BOXES_WITH_COMMANDERS.LABEL,
                                link: value.NFT_BOXES_WITH_COMMANDERS.PATH,
                              },
                              {
                                label: value.NFT_BOXES_WITH_TURRETS_2.LABEL,
                                link: value.NFT_BOXES_WITH_TURRETS_2.PATH,
                              },
                              {
                                label: value.NFT_BOXES_WITH_COMMANDERS_2.LABEL,
                                link: value.NFT_BOXES_WITH_COMMANDERS_2.PATH,
                              },
                            ]}
                            type="link"
                          />
                        </li>
                      </>
                    ) : key === "STAKING" ? (
                      <>
                        <li className="nav-item">
                          <MenuList
                            name="STAKING"
                            items={[
                              {
                                label: PATHS.STAKING.LABEL,
                                link: PATHS.STAKING.PATH,
                              },
                              {
                                label: PATHS.STAKING_NFT.LABEL,
                                link: PATHS.STAKING_NFT.PATH,
                              },
                            ]}
                            type="link"
                          />
                        </li>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                )}
              </React.Fragment>
            ))}
          </>
        )}

        {!isDownSm && (
          <li className="nav-item">
            <Tooltip title={"Coming soon..."}>
              <span>
                <CtaButton variant="outlined" onClick={send} disabled>
                  Buy ZTD
                </CtaButton>
              </span>
            </Tooltip>
          </li>
        )}

        {isDownSm && user.address && (
          <li className="nav-item">
            <Tooltip title={"Coming soon..."}>
              <span>
                <CtaButton variant="outlined" onClick={send} disabled>
                  Buy ZTD
                </CtaButton>
              </span>
            </Tooltip>
          </li>
        )}

        <li className="nav-item">
          {user.address && !isDownSm && (
            <>
              <MenuList
                name={`${user.address.substring(0, 3)}...${user.address.slice(-4)}`}
                items={[
                  {
                    label: PATHS.ACCOUNT.LABEL,
                    link: PATHS.ACCOUNT.PATH,
                  },
                  {
                    label: PATHS.TRANSACTIONS.LABEL,
                    link: PATHS.TRANSACTIONS.PATH,
                  },
                  {
                    label: PATHS.REFERAL.LABEL,
                    link: PATHS.REFERAL.PATH,
                  },
                  {
                    label: "Log out",
                    action: disconnect,
                    hasDivider: true,
                  },
                ]}
                type="button"
              />
            </>
          )}

          {!user.address && (
            <>
              {loading ? (
                <LoadingButton loading variant="contained">
                  Submit
                </LoadingButton>
              ) : (
                <Tooltip title={connectButtonState.tooltip}>
                  <span>
                    <CtaButton
                      variant="contained"
                      onClick={getWallet}
                      disabled={loading}
                      size="small"
                    >
                      {connectButtonState.text}
                    </CtaButton>
                  </span>
                </Tooltip>
              )}
            </>
          )}
        </li>

        {isDownLg && (
          <li className="nav-item">
            <DrawerMenu />
          </li>
        )}
      </ul>
    </nav>
  )
}
